/* global axios */

import Cookies from 'js-cookie';
import endPoints from './endPoints';
import {
  clearCookiesOnLogout,
  deleteIndexedDBOnLogout,
} from '../store/utils/api';
import { broadcastAuthSignal } from '../helper/authSignal';
import { getHeldAuth } from '../helper/authSync';

let logoutRequest = null;

export default {
  validityCheck() {
    const urlData = endPoints('validityCheck');
    return axios.get(urlData.url);
  },
  // One sign-out per tab, even if the menu and a `user:logout` event both ask.
  logout() {
    if (logoutRequest) return logoutRequest;
    const urlData = endPoints('logout');
    const ownUid = getHeldAuth()?.uid;
    // The other tabs of this account follow without calling sign_out themselves.
    const tellOtherTabs = () =>
      broadcastAuthSignal({ type: 'logout', uid: ownUid });
    logoutRequest = axios
      .delete(urlData.url)
      .then(response => {
        tellOtherTabs();
        deleteIndexedDBOnLogout();
        clearCookiesOnLogout(null, { ownUid });
        return response;
      })
      .catch(error => {
        // 401/404: the token is already gone, so the session is over everywhere.
        if ([401, 404].includes(error?.response?.status)) tellOtherTabs();
        // Even if backend session is already invalid and sign_out returns 401,
        // force local logout to avoid a dead session loop in UI.
        deleteIndexedDBOnLogout();
        clearCookiesOnLogout(null, { ownUid });
        throw error;
      });
    return logoutRequest;
  },
  hasAuthCookie() {
    return !!Cookies.get('cw_d_session_info');
  },
  getAuthData() {
    if (this.hasAuthCookie()) {
      const savedAuthInfo = Cookies.get('cw_d_session_info');
      return JSON.parse(savedAuthInfo || '{}');
    }
    return false;
  },
  profileUpdate({ displayName, avatar, ...profileAttributes }) {
    const formData = new FormData();
    Object.keys(profileAttributes).forEach(key => {
      const hasValue = profileAttributes[key] === undefined;
      if (!hasValue) {
        formData.append(`profile[${key}]`, profileAttributes[key]);
      }
    });
    formData.append('profile[display_name]', displayName || '');
    if (avatar) {
      formData.append('profile[avatar]', avatar);
    }
    return axios.put(endPoints('profileUpdate').url, formData);
  },

  profilePasswordUpdate({ currentPassword, password, passwordConfirmation }) {
    return axios.put(endPoints('profileUpdate').url, {
      profile: {
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
      },
    });
  },

  updateUISettings({ uiSettings }) {
    return axios.put(endPoints('profileUpdate').url, {
      profile: { ui_settings: uiSettings },
    });
  },

  updateAvailability(availabilityData) {
    return axios.post(endPoints('availabilityUpdate').url, {
      profile: { ...availabilityData },
    });
  },

  updateAcceptsChat(accountId, acceptsChat = true) {
    return axios.post(endPoints('acceptsChat').url, {
      profile: { account_id: accountId, accepts_chat: acceptsChat },
    });
  },

  deleteAvatar() {
    return axios.delete(endPoints('deleteAvatar').url);
  },

  updateAccountAvatar({ accountId, avatar }) {
    const formData = new FormData();
    formData.append('profile[account_id]', accountId);
    formData.append('profile[avatar]', avatar);
    return axios.post(endPoints('accountAvatar').url, formData);
  },

  deleteAccountAvatar({ accountId }) {
    return axios.delete(endPoints('accountAvatar').url, {
      params: { 'profile[account_id]': accountId },
    });
  },

  resetPassword({ email }) {
    const urlData = endPoints('resetPassword');
    return axios.post(urlData.url, { email });
  },

  setActiveAccount({ accountId }) {
    const urlData = endPoints('setActiveAccount');
    return axios.put(urlData.url, {
      profile: {
        account_id: accountId,
      },
    });
  },
  resendConfirmation() {
    const urlData = endPoints('resendConfirmation');
    return axios.post(urlData.url);
  },
  resetAccessToken() {
    const urlData = endPoints('resetAccessToken');
    return axios.post(urlData.url);
  },
};
